<template>
    <LoadingView v-if="loading" />
    <article v-else class="bg-white shadow sm:rounded">
        <header class="px-4 py-5 border-b border-oakwood-gray-200 sm:px-6 flex items-center justify-between gap-8 sticky top-0 bg-white">
            <h1 class="text-3xl leading-9 font-headline antialiased font-medium text-bhav-blue-700">
                Add Promotion
            </h1>
            <nav class="flex flex-none gap-4 justify-end items-center">
                <FormButton color="gray" @click="$router.go(-1)">Cancel</FormButton>
                <FormButton @click="save" :disabled="!valid" color="blue">Save</FormButton>
            </nav>
        </header>

        <form autocomplete="off" class="grid grid-cols-1 p-5 gap-5">
            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="headline" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Headline <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <TextInput id="headline" v-model="promotion.headline" placeholder="Promotion headline" maxlength="255" class="col-span-3" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Type <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <div class="col-span-3">
                    <MultiselectField
                        label="Select Type"
                        :show-search="false"
                        :show-create="false"
                        :multiple="false"
                        v-model="promotion.type"
                        color="orange"
                        :options="Promotion.types"
                        instructions="Select a type" />
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="description" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Description <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <textarea class="col-span-3 bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="description" v-model="promotion.description" maxlength="1000"></textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="button_text" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Button Text <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <TextInput id="button_text" v-model="promotion.button_text" placeholder="Button Text" maxlength="255" class="col-span-3" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="open_in_new_tab" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Open in New Tab
                </label>
                <ToggleButton label="Open in New Tab" :checked="promotion.open_in_new_tab" @changed="promotion.open_in_new_tab = $event" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="disclaimer" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Disclaimer
                </label>
                <textarea class="col-span-3 bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="disclaimer" v-model="promotion.disclaimer" maxlength="1000"></textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="start_at" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Start Date
                </label>
                <TextInput id="start_at" type="datetime-local" v-model="start_at" class="col-span-3 max-w-80" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="end_at" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    End Date
                </label>
                <TextInput id="end_at" type="datetime-local" v-model="end_at" class="col-span-3 max-w-80" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="url" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    URL
                </label>
                <URLField id="url" v-model="promotion.url" placeholder="https://example.com" class="col-span-3 max-w-80" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Small Image
                </label>
                <div class="col-span-3">
                    <UploadFiles v-model="small_image" :multiple="false" />
                    <TextInput v-model="promotion.meta.small_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Large Image <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
                </label>
                <div class="col-span-3">
                    <UploadFiles v-model="large_image" :multiple="false" />
                    <TextInput v-model="promotion.meta.large_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 pt-5">
                <label for="show_image_inline" class="block text-sm font-medium leading-6 text-bhav-blue-300">
                    Show image inline on page
                </label>
                <ToggleButton label="Show image inline on page" :checked="promotion.meta.show_image_inline" @changed="promotion.meta.show_image_inline = $event" />
            </div>
        </form>
    </article>
</template>

<script>
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, TextInput, ToggleButton, URLField, UploadFiles } from '@/components/form';
import MultiselectField from '@/components/form/MultiselectField.vue';
import Promotion from '@/models/promotion';
import DateTime from '@/helpers/date';
import { PromotionsController } from '@/controllers/promotions';

export default {
    setup() {
        return { Promotion }
    },
    data() {
        return {
            loading: false,
            promotion: new Promotion()
        }
    },
    computed: {
        start_at: {
            get() { return DateTime.toDateTimeInput(this.promotion._start_at); },
            set(value) { this.promotion.start_at = value; }
        },
        end_at: {
            get() { return DateTime.toDateTimeInput(this.promotion._end_at); },
            set(value) { this.promotion.end_at = value; }
        },
        small_image: {
            get() {
                return this.promotion.meta.small_image ? [this.promotion.meta.small_image] : [];
            },
            set(value) {
                this.promotion.meta.small_image = value[0] ?? null;
            }
        },
        large_image: {
            get() {
                return this.promotion.meta.large_image ? [this.promotion.meta.large_image] : [];
            },
            set(value) {
                this.promotion.meta.large_image = value[0] ?? null;
            }
        },
        valid() {
            const hasSmallImage = !!this.promotion.meta.small_image;
            return this.promotion.headline?.length > 0 &&
                this.promotion.description?.length > 0 &&
                this.promotion.type != null &&
                !!this.promotion.meta.large_image &&
                this.promotion.meta.large_image_alt?.length > 0 &&
                (!hasSmallImage || this.promotion.meta.small_image_alt?.length > 0);
        }
    },
    methods: {
        save() {
            if (!this.valid) return;
            this.loading = true;
            PromotionsController.store(this.promotion)
                .then(promotion => {
                    this.$router.push({ name: 'promotion-show', params: { id: promotion.id } });
                })
                .catch(err => {
                    this.loading = false;
                    console.error(err);
                });
        }
    },
    components: {
        LoadingView, FormButton, TextInput, ToggleButton, URLField, UploadFiles, MultiselectField
    }
}
</script>
